import { ReactElement } from "react";
import { t } from "@lingui/core/macro";

import { Icon } from "_/components/icon";

import * as S from "./styled";

/** Result rows rendered per page. */
export const PAGE_SIZE = 100;

/**
 * Entries the control renders once there are more pages than will fit. Fixed so
 * the control keeps one width and the buttons don't reflow under the cursor.
 */
export const PAGE_SLOTS = 7;

/** Stands in for a run of page numbers the control leaves out. */
export const PAGE_GAP = "gap";

export type PageItem = number | typeof PAGE_GAP;

/** Number of pages a result of `rowCount` rows spans. */
export const pageCount = (rowCount: number) => Math.ceil(rowCount / PAGE_SIZE);

const run = (from: number, to: number) =>
  Array.from({ length: to - from + 1 }, (_, i) => from + i);

/**
 * Page numbers to offer, given how many pages there are and which is shown.
 *
 * Every result is one of three shapes, each exactly `PAGE_SLOTS` wide:
 *
 * ```
 * near the start   1 2 3 4 5 … 40
 * in the middle    1 … 19 20 21 … 40
 * near the end     1 … 36 37 38 39 40
 * ```
 *
 * The thresholds are picked so an ellipsis always stands for at least two
 * pages; a `…` hiding a single number reads like a bug.
 */
export function pageList(current: number, pages: number): PageItem[] {
  if (pages <= PAGE_SLOTS) return run(1, pages);

  if (current <= 4) return [...run(1, 5), PAGE_GAP, pages];

  if (current >= pages - 3) return [1, PAGE_GAP, ...run(pages - 4, pages)];

  return [1, PAGE_GAP, ...run(current - 1, current + 1), PAGE_GAP, pages];
}

/**
 * Page numbers for the result table. Results are paged so only one page is ever
 * in the DOM, since rendering every row of a large result makes the browser
 * crawl.
 */
export function Pagination({
  page,
  pages,
  onSelect,
}: {
  page: number;
  pages: number;
  onSelect: (page: number) => void;
}): ReactElement {
  const items = pageList(page, pages).map((item, i) => {
    if (item === PAGE_GAP) {
      // Index is a stable key here: a gap only moves when the whole list is
      // rebuilt from `page` and `pages` anyway.
      return <S.PageGap key={`gap-${i}`}>…</S.PageGap>;
    }

    return (
      <S.PageButton
        key={item}
        type="button"
        aria-current={item === page ? "page" : undefined}
        onClick={() => onSelect(item)}
        $current={item === page}
      >
        {item}
      </S.PageButton>
    );
  });

  return (
    <S.Pagination aria-label={t`components.telemetry-console.pages`}>
      <S.PageStep
        type="button"
        aria-label={t`common.previous`}
        disabled={page === 1}
        onClick={() => onSelect(page - 1)}
        $current={false}
      >
        <Icon variant="ChevronLeft" />
      </S.PageStep>
      {items}
      <S.PageStep
        type="button"
        aria-label={t`common.next`}
        disabled={page === pages}
        onClick={() => onSelect(page + 1)}
        $current={false}
      >
        <Icon variant="ChevronRight" />
      </S.PageStep>
    </S.Pagination>
  );
}
